import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Heart, Sparkles, Check, Star, Shield, Download, Clock, Users, ChevronDown, Plus, Minus, ShoppingCart, Instagram, Facebook, Youtube } from 'lucide-react';
import { EbookPromotion } from '../components/EbookPromotion';
import { BonusSlideshow } from '../components/BonusSlideshow';
import { LegalModal } from '../components/LegalModal';
function SectionReveal({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 40
  }} transition={{
    duration: 0.8,
    delay,
    ease: 'easeOut'
  }} data-id="element-343">
      {children}
    </motion.div>;
}
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [legalModal, setLegalModal] = useState<'support' | 'contact' | 'terms' | 'privacy' | null>(null);
  const heroRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const faqs = [{
    question: '¿Realmente solo necesito 5 minutos al día?',
    answer: 'Sí. Este programa está diseñado específicamente para mamás con cero tiempo. Cada ejercicio toma entre 3-7 minutos. No necesitas una hora de meditación ni un spa. Solo 5 minutos que puedes hacer mientras esperas que hierva el agua o en el baño.'
  }, {
    question: '¿Qué pasa si no soy constante?',
    answer: 'El programa está diseñado para la vida real, no para la perfección. Si te saltas días, no pasa nada. Retomas donde lo dejaste. No hay culpa, no hay "empezar de cero". La idea es progreso, no perfección.'
  }, {
    question: '¿Funciona si tengo hijos pequeños?',
    answer: 'Especialmente funciona si tienes hijos pequeños. Muchas mamás lo usan durante la siesta, en el baño, o mientras los niños ven 5 minutos de pantalla.'
  }, {
    question: '¿Qué formato tiene el ebook?',
    answer: 'Es un PDF descargable que puedes leer en tu teléfono, tablet o computadora. También puedes imprimirlo si prefieres. Acceso inmediato después de la compra.'
  }, {
    question: '¿Hay garantía de devolución?',
    answer: 'Sí, garantía de 30 días sin preguntas. Si no te funciona, te devolvemos tu dinero completo. Queremos que te sientas segura con esta inversión en ti misma.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-344">
      {/* Sticky Header */}
      <motion.header initial={{
      y: -100
    }} animate={{
      y: 0
    }} className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50" data-id="element-345">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-346">
          <div className="flex items-center gap-2" data-id="element-347">
            <Heart className="w-6 h-6 text-primary" fill="currentColor" data-id="element-348" />
            <span className="font-serif text-xl text-text-main" data-id="element-349">
              Autocuidado 5 Min
            </span>
          </div>
        </div>
      </motion.header>

      {/* Hero Section - HOOK */}
      <section ref={heroRef} className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 overflow-hidden bg-gradient-to-b from-amber-50/30 to-white" data-id="element-350">
        <div className="max-w-7xl mx-auto" data-id="element-351">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center" data-id="element-352">
            {/* Mobile: Image First */}
            <motion.div style={{
            y
          }} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative w-full max-w-sm mx-auto lg:hidden order-1" data-id="element-353">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-354">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1jsB17hWNL7C56Uf3uwzUm/cover_autocuidado.png" alt="Autocuidado de 5 Minutos para Mamás Agotadas" className="w-full h-auto" data-id="element-355" />
              </div>
            </motion.div>

            {/* Content - Order 2 on mobile, Order 1 on desktop */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-356">
              {/* Animated Badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="inline-block mb-4 sm:mb-6" data-id="element-357">
                <motion.span animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-xs font-bold bg-primary/10 px-3 py-1.5 rounded-full inline-block" style={{
                color: '#C53030'
              }} data-id="element-358">
                  TU RESET DE MAMÁ
                </motion.span>
              </motion.div>

              {/* Main Headline - Hypnotic */}
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif mb-4 sm:mb-6 leading-tight" style={{
              color: '#C53030'
            }} data-id="element-359">
                Autocuidado de 5 Minutos para Mamás Agotadas
              </motion.h1>

              {/* Subheadline - Direct Benefit */}
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 leading-relaxed" style={{
              color: '#C53030'
            }} data-id="element-360">
                Tu reset de mamá para salir del modo supervivencia en solo 5
                minutos al día
              </motion.p>

              {/* Hypnotic Trance Section */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6,
              duration: 1
            }} className="relative mb-6 sm:mb-8" data-id="element-361">
                {/* Soft Glow Effect */}
                <motion.div animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute inset-0 bg-gradient-to-r from-amber-200/30 via-primary/20 to-amber-200/30 blur-2xl rounded-3xl -z-10" data-id="element-362" />

                <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-100/50 shadow-xl" data-id="element-363">
                  {/* Opening - Hypnotic Language */}
                  <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.8,
                  duration: 1.5
                }} className="text-lg sm:text-xl leading-relaxed italic mb-4 sm:mb-6" style={{
                  color: '#2B2B2B'
                }} data-id="element-364">
                    <span className="font-semibold" style={{
                    color: '#C53030'
                  }} data-id="element-365">
                      Cierra los ojos un momento.
                    </span>
                  </motion.p>

                  <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 1.2,
                  duration: 1.5
                }} className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6" style={{
                  color: '#2B2B2B'
                }} data-id="element-366">
                    Siente el nudo en la garganta, los hombros tensos, la culpa
                    dando vueltas en tu cabeza.
                  </motion.p>

                  {/* Reframe */}
                  <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 1.6,
                  duration: 1.5
                }} className="text-lg sm:text-xl leading-relaxed font-semibold mb-4 sm:mb-6" style={{
                  color: '#2B2B2B'
                }} data-id="element-367">
                    No estás rota ni eres mala mamá:{' '}
                    <span style={{
                    color: '#C53030'
                  }} data-id="element-368">
                      estás agotada.
                    </span>
                  </motion.p>

                  {/* Future Pacing Question */}
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 2,
                  duration: 1.5
                }} className="bg-gradient-to-r from-primary/5 to-amber-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-l-4" style={{
                  borderLeftColor: '#C53030'
                }} data-id="element-369">
                    <p className="text-lg sm:text-xl leading-relaxed font-medium" style={{
                    color: '#2B2B2B'
                  }} data-id="element-370">
                      ¿Qué pasaría si cada día tuvieras un pequeño botón de
                      reset…
                    </p>
                    <p className="text-base sm:text-lg leading-relaxed mt-3" style={{
                    color: '#2B2B2B'
                  }} data-id="element-371">
                      5 minutos para bajar el ruido, soltar un poco de culpa y
                      volver a sentir calma,{' '}
                      <span className="font-semibold" style={{
                      color: '#C53030'
                    }} data-id="element-372">
                        sin pedir horas libres, sin spa y sin sentirte egoísta?
                      </span>
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 2.4
            }} className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm" style={{
              color: '#2B2B2B'
            }} data-id="element-373">
                <div className="flex items-center gap-1" data-id="element-374">
                  <Download className="w-4 h-4" data-id="element-375" />
                  <span data-id="element-376">Descarga instantánea</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-377">
                  <Shield className="w-4 h-4" data-id="element-378" />
                  <span data-id="element-379">Garantía 30 días</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Desktop: Image on Right (hidden on mobile) */}
            <motion.div style={{
            y
          }} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative hidden lg:block order-2" data-id="element-380">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-381">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1jsB17hWNL7C56Uf3uwzUm/cover_autocuidado.png" alt="Autocuidado de 5 Minutos para Mamás Agotadas" className="w-full h-auto" data-id="element-382" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5
      }} className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2" data-id="element-383">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2
        }} data-id="element-384">
            <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-text-muted" data-id="element-385" />
          </motion.div>
        </motion.div>
      </section>

      {/* Hidden Knowledge Section */}
      <section className="py-20 px-6 bg-white" data-id="element-386">
        <div className="max-w-5xl mx-auto" data-id="element-387">
          <SectionReveal data-id="element-388">
            <div className="text-center mb-12" data-id="element-389">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-390">
                La Carga Invisible
              </h2>
              <p className="text-xl text-text-muted max-w-3xl mx-auto leading-relaxed" data-id="element-391">
                Millones de mamás viven con lo que llamo "la carga invisible":
                cansancio físico que aplasta tu espalda, saturación mental que
                convierte la cabeza en un pozo sin fondo, desgaste emocional que
                apaga la ternura hasta convertirla en esfuerzo.
              </p>
            </div>
          </SectionReveal>

          <SectionReveal delay={0.3} data-id="element-392">
            <div className="bg-gradient-to-br from-amber-50 to-stone-50 rounded-3xl p-8 md:p-12 text-center" data-id="element-393">
              <p className="text-2xl md:text-3xl font-serif text-text-main mb-6 leading-relaxed" data-id="element-394">
                Pero la ciencia lo sabe:
              </p>
              <p className="text-xl text-text-main leading-relaxed mb-6" data-id="element-395">
                Pausas conscientes — incluso de 3 a 5 minutos — repetidas día
                tras día, hacen más por tu calma y energía que un día de spa al
                año.
              </p>
              <p className="text-2xl font-bold text-primary" data-id="element-396">
                No magia. Biología.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Pain + Programming Section */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-397">
        <div className="max-w-6xl mx-auto" data-id="element-398">
          <SectionReveal data-id="element-399">
            <div className="text-center mb-16" data-id="element-400">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-401">
                Quizá Hoy...
              </h2>
            </div>
          </SectionReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-402">
            <SectionReveal delay={0.2} data-id="element-403">
              <div className="space-y-6" data-id="element-404">
                <div className="bg-white rounded-2xl p-6 shadow-md" data-id="element-405">
                  <p className="text-lg text-text-main leading-relaxed" data-id="element-406">
                    Gritaste sin querer.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-md" data-id="element-407">
                  <p className="text-lg text-text-main leading-relaxed" data-id="element-408">
                    Te encerraste al baño a llorar en silencio.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-md" data-id="element-409">
                  <p className="text-lg text-text-main leading-relaxed" data-id="element-410">
                    Te dormiste con la cabeza zumbando, pensando "no basta", "no
                    estoy haciendo bien las cosas".
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.4} data-id="element-411">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-412">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qr7FG9abWX8ryXXbXyQNyE/imagen_4.png" alt="No necesitas una hora, solo 5 minutos guiados" className="w-full h-auto" data-id="element-413" />
              </div>
            </SectionReveal>
          </div>

          <SectionReveal delay={0.6} data-id="element-414">
            <div className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-xl" data-id="element-415">
              <p className="text-2xl md:text-3xl font-serif text-text-main mb-6 leading-relaxed" data-id="element-416">
                Pero eso no te define.
              </p>
              <p className="text-xl text-text-main leading-relaxed mb-4" data-id="element-417">
                No eres mala madre.{' '}
                <span className="font-bold" data-id="element-418">Estás exhausta.</span>
              </p>
              <p className="text-xl text-primary font-semibold" data-id="element-419">
                Y ese agotamiento te grita: "Haz algo por ti".
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Reframe Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-stone-100" data-id="element-420">
        <div className="max-w-6xl mx-auto" data-id="element-421">
          <SectionReveal data-id="element-422">
            <div className="text-center mb-12" data-id="element-423">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-424">
                Imagina Redefinir Tu Identidad
              </h2>
            </div>
          </SectionReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-425">
            <SectionReveal delay={0.2} data-id="element-426">
              <div data-id="element-427">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-6 border-l-4 border-gray-300" data-id="element-428">
                  <p className="text-xl text-text-muted line-through mb-2" data-id="element-429">
                    Mamá agotada que nunca alcanza
                  </p>
                </div>
                <div className="flex items-center justify-center mb-6" data-id="element-430">
                  <ChevronDown className="w-8 h-8 text-primary" data-id="element-431" />
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-xl border-l-4 border-primary" data-id="element-432">
                  <p className="text-2xl font-serif text-text-main mb-4" data-id="element-433">
                    Mamá consciente que se sostiene desde adentro
                  </p>
                  <p className="text-lg text-text-muted" data-id="element-434">
                    Solo necesitas 5 minutos — tú, intacta, viva, recobrando
                    aire.
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.4} data-id="element-435">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-436">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hJL46Mr5eKpHcEVkgLReh5/imagen_5.png" alt="Pensé que no tenía tiempo para mí - Testimonio de Carla" className="w-full h-auto" data-id="element-437" />
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* Micro Trance Section */}
      <section className="py-20 px-6 bg-white" data-id="element-438">
        <div className="max-w-4xl mx-auto" data-id="element-439">
          <SectionReveal data-id="element-440">
            <div className="text-center mb-12" data-id="element-441">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-8" data-id="element-442">
                Cierra los Ojos un Instante
              </h2>

              <div className="space-y-8 text-left" data-id="element-443">
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8
              }} className="bg-gradient-to-r from-amber-50 to-transparent rounded-2xl p-8" data-id="element-444">
                  <p className="text-xl text-text-main leading-relaxed italic" data-id="element-445">
                    Respira. Siente cómo esos 5 minutos limpian un rincón de tu
                    mente.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.2
              }} className="bg-gradient-to-l from-stone-50 to-transparent rounded-2xl p-8" data-id="element-446">
                  <p className="text-xl text-text-main leading-relaxed italic" data-id="element-447">
                    Ese suspiro que baja por tu cuello, que suelta los hombros.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.4
              }} className="bg-gradient-to-r from-amber-50 to-transparent rounded-2xl p-8" data-id="element-448">
                  <p className="text-xl text-text-main leading-relaxed italic" data-id="element-449">
                    Ese instante que te recuerda quién eres — antes de mamá:
                    mujer, ser de vida, merecedora de paz.
                  </p>
                </motion.div>
              </div>
            </div>
          </SectionReveal>

          <SectionReveal delay={0.6} data-id="element-450">
            <div className="bg-primary/5 rounded-3xl p-8 md:p-12 mt-12" data-id="element-451">
              <h3 className="text-3xl font-serif text-text-main mb-6 text-center" data-id="element-452">
                Y ahora visualiza:
              </h3>
              <div className="space-y-4" data-id="element-453">
                <div className="flex items-start gap-4" data-id="element-454">
                  <Check className="w-6 h-6 text-primary flex-shrink-0 mt-1" data-id="element-455" />
                  <p className="text-lg text-text-main" data-id="element-456">
                    El día que empieza sin prisa, con una intención clara.
                  </p>
                </div>
                <div className="flex items-start gap-4" data-id="element-457">
                  <Check className="w-6 h-6 text-primary flex-shrink-0 mt-1" data-id="element-458" />
                  <p className="text-lg text-text-main" data-id="element-459">
                    La noche que termina sin culpa, sin listas dando vueltas en
                    la cabeza.
                  </p>
                </div>
                <div className="flex items-start gap-4" data-id="element-460">
                  <Check className="w-6 h-6 text-primary flex-shrink-0 mt-1" data-id="element-461" />
                  <p className="text-lg text-text-main" data-id="element-462">
                    Ese pequeño gesto que eliges hoy… Es semilla de serenidad
                    mañana.
                  </p>
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Future Pacing Section */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-463">
        <div className="max-w-6xl mx-auto" data-id="element-464">
          <SectionReveal data-id="element-465">
            <div className="text-center mb-16" data-id="element-466">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-467">
                Dentro de Una Semana
              </h2>
              <p className="text-2xl text-text-muted" data-id="element-468">Notarás — algo cambió.</p>
            </div>
          </SectionReveal>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-469">
            <SectionReveal delay={0.2} data-id="element-470">
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-471">
                <h3 className="text-2xl font-serif text-text-main mb-4" data-id="element-472">
                  No será dramático. Será sutil.
                </h3>
                <ul className="space-y-3" data-id="element-473">
                  <li className="flex items-start gap-3" data-id="element-474">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-1" data-id="element-475" />
                    <span className="text-text-muted" data-id="element-476">
                      Un respiro que dura un minuto más.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-477">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-1" data-id="element-478" />
                    <span className="text-text-muted" data-id="element-479">
                      Un "no gracias" que sale sin culpa.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-480">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-1" data-id="element-481" />
                    <span className="text-text-muted" data-id="element-482">
                      Una mirada más suave a tus hijos.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-483">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-1" data-id="element-484" />
                    <span className="text-text-muted" data-id="element-485">
                      Un poco más de paciencia, un poco más de ti.
                    </span>
                  </li>
                </ul>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.4} data-id="element-486">
              <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-2xl p-8 shadow-lg" data-id="element-487">
                <h3 className="text-2xl font-serif mb-4" data-id="element-488">Y en treinta días…</h3>
                <p className="text-xl leading-relaxed" data-id="element-489">
                  Un espacio interior que antes habitaba el caos, ahora respira.
                </p>
              </div>
            </SectionReveal>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-490">
            <SectionReveal delay={0.6} data-id="element-491">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-492">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tRzQAQNNSY1qCxssDN8HHb/imagen_3.png" alt="Rutina de 5 minutos antes de dormir" className="w-full h-auto" data-id="element-493" />
              </div>
            </SectionReveal>

            <SectionReveal delay={0.8} data-id="element-494">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-495">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aQAKwMER4omW8mUoFHj9P8/imagen_2.png" alt="Testimonio de transformación" className="w-full h-auto" data-id="element-496" />
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* Promotion Section */}
      <section className="py-20 px-6 bg-white" data-id="element-497">
        <div className="max-w-6xl mx-auto" data-id="element-498">
          <EbookPromotion data-id="element-499" />
        </div>
      </section>

      {/* Bonus Slideshow Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-stone-100" data-id="element-500">
        <div className="max-w-7xl mx-auto" data-id="element-501">
          <SectionReveal data-id="element-502">
            <div className="text-center mb-12" data-id="element-503">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-504">
                Descubre Cada Bono
              </h2>
              <p className="text-xl text-text-muted max-w-2xl mx-auto" data-id="element-505">
                Explora las herramientas prácticas que transformarán tu día a
                día
              </p>
            </div>
          </SectionReveal>

          <SectionReveal delay={0.3} data-id="element-506">
            <BonusSlideshow data-id="element-507" />
          </SectionReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-white" data-id="element-508">
        <div className="max-w-3xl mx-auto" data-id="element-509">
          <SectionReveal data-id="element-510">
            <div className="text-center mb-16" data-id="element-511">
              <h2 className="text-4xl md:text-5xl font-serif text-text-main mb-6" data-id="element-512">
                Preguntas Frecuentes
              </h2>
            </div>
          </SectionReveal>

          <div className="space-y-4" data-id="element-513">
            {faqs.map((faq, index) => <SectionReveal key={index} delay={index * 0.1} data-id="element-514">
                <div className="bg-gray-50 rounded-2xl overflow-hidden" data-id="element-515">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-516">
                    <span className="text-lg font-semibold text-text-main pr-8" data-id="element-517">
                      {faq.question}
                    </span>
                    <motion.div animate={{
                  rotate: openFaq === index ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-518">
                      {openFaq === index ? <Minus className="w-5 h-5 text-primary flex-shrink-0" data-id="element-519" /> : <Plus className="w-5 h-5 text-primary flex-shrink-0" data-id="element-520" />}
                    </motion.div>
                  </button>
                  <motion.div initial={false} animate={{
                height: openFaq === index ? 'auto' : 0,
                opacity: openFaq === index ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-521">
                    <div className="px-8 pb-6" data-id="element-522">
                      <p className="text-text-muted leading-relaxed" data-id="element-523">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </SectionReveal>)}
          </div>
        </div>
      </section>

      {/* Final CTA - Call to Action */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-amber-50 to-stone-50" data-id="element-524">
        <div className="max-w-4xl mx-auto" data-id="element-525">
          <SectionReveal data-id="element-526">
            <div className="text-center mb-8 sm:mb-12" data-id="element-527">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-transparent bg-clip-text bg-gradient-to-r from-pink-600 via-rose-600 to-red-600 mb-4 sm:mb-6" data-id="element-528">
                Permítete Ese Primer Minuto
              </h2>
              <p className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-6 sm:mb-8" data-id="element-529">
                Haz clic ahora. Descarga tu ebook. Empieza hoy mismo tu ritual
                de 5 minutos.
              </p>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl text-center" data-id="element-530">
              <div className="mb-6 sm:mb-8" data-id="element-531">
                <div className="inline-block relative" data-id="element-532">
                  <span className="text-2xl sm:text-3xl text-text-muted line-through block mb-2" data-id="element-533">
                    $250.00 USD
                  </span>
                  <span className="text-5xl sm:text-6xl font-bold text-primary" data-id="element-534">
                    $9.95
                  </span>
                </div>
                <p className="text-base sm:text-lg text-text-muted mt-4" data-id="element-535">
                  Inversión única • Acceso inmediato • Garantía 30 días
                </p>
              </div>

              <motion.a href="https://mikioscodigital.com/cart/51727945761049:1" target="_blank" rel="noopener" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-gradient-to-r from-pink-500 to-blue-500 hover:from-pink-600 hover:to-blue-600 text-white px-8 sm:px-12 py-5 sm:py-6 rounded-full text-lg sm:text-xl font-bold shadow-xl transition-all mb-6 flex items-center justify-center gap-3 mx-auto cursor-pointer w-full sm:w-auto max-w-md" data-id="element-536">
                <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-537" />
                <span className="text-center" data-id="element-538">
                  QUIERO MI PLAN DE AUTOCUIDADO HOY
                </span>
              </motion.a>

              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-text-muted mt-6" data-id="element-539">
                <div className="flex items-center gap-2" data-id="element-540">
                  <Check className="w-4 h-4 text-primary" data-id="element-541" />
                  <span data-id="element-542">Descarga instantánea</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-543">
                  <Check className="w-4 h-4 text-primary" data-id="element-544" />
                  <span data-id="element-545">Plan de 30 días completo</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-546">
                  <Check className="w-4 h-4 text-primary" data-id="element-547" />
                  <span data-id="element-548">Garantía 30 días</span>
                </div>
              </div>
            </div>

            <div className="mt-8 sm:mt-12 text-center px-4" data-id="element-549">
              <p className="text-xl sm:text-2xl font-serif text-text-main mb-3 sm:mb-4" data-id="element-550">
                Transforma el caos en calma.
              </p>
              <p className="text-lg sm:text-xl text-primary font-semibold" data-id="element-551">
                Y vuelve a respirar — libre, con poder, con paz.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 sm:py-10 md:py-12 px-4 sm:px-6" data-id="element-552">
        <div className="max-w-6xl mx-auto text-center" data-id="element-553">
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6" data-id="element-554">
            <Heart className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" data-id="element-555" />
            <span className="font-serif text-lg sm:text-xl" data-id="element-556">
              Autocuidado 5 Min
            </span>
          </div>

          {/* Social Media Icons */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-557">
            <a href="https://instagram.com/kiosscodigital" target="_blank" rel="noopener noreferrer" className="group" aria-label="Instagram" data-id="element-558">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group-hover:scale-110" data-id="element-559">
                <Instagram className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-560" />
              </div>
            </a>
            <a href="https://www.facebook.com/contactokd/" target="_blank" rel="noopener noreferrer" className="group" aria-label="Facebook" data-id="element-561">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group-hover:scale-110" data-id="element-562">
                <Facebook className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-563" />
              </div>
            </a>
            <a href="https://www.youtube.com/@mikioscodigital" target="_blank" rel="noopener noreferrer" className="group" aria-label="YouTube" data-id="element-564">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group-hover:scale-110" data-id="element-565">
                <Youtube className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-566" />
              </div>
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6 sm:mb-8 text-xs sm:text-sm" data-id="element-567">
            <button onClick={() => setLegalModal('support')} className="hover:text-primary transition-colors" data-id="element-568">
              Soporte
            </button>
            <button onClick={() => setLegalModal('contact')} className="hover:text-primary transition-colors" data-id="element-569">
              Contacto
            </button>
            <button onClick={() => setLegalModal('terms')} className="hover:text-primary transition-colors" data-id="element-570">
              Términos
            </button>
            <button onClick={() => setLegalModal('privacy')} className="hover:text-primary transition-colors" data-id="element-571">
              Privacidad
            </button>
          </div>

          <p className="text-xs sm:text-sm text-gray-400 px-4" data-id="element-572">
            © 2024 Autocuidado para Mamás • Todos los Derechos Reservados
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      {legalModal && <LegalModal isOpen={true} onClose={() => setLegalModal(null)} type={legalModal} data-id="element-573" />}
    </div>;
}